"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _I18nManager = _interopRequireDefault(require("../../../managers/I18nManager"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

//
// Copyright (c) 2019 Nutanix Inc. All rights reserved.
//
var _default = {
  /**
   * Parse time string value into object with hours, minutes and seconds in 24 hour time
   * @param {string} value - Time value
   * @param {object} props - TimePicker props
   * @returns {{hours: *, minutes: *, seconds: *}} - Time object
   */
  parseTimeValue: function parseTimeValue(value, props) {
    var timeObj;

    if (props.parseInputValue) {
      timeObj = props.parseInputValue(value);
    } else {
      if (!value) {
        // Empty value is considered valid
        return {
          error: false,
          errorMessage: ''
        };
      }

      timeObj = this.parseInputValue(value, props);
    } // If error is already defined, there's no need to go through validation below


    if (timeObj.error) {
      return timeObj;
    }

    if (!this.validateHours(timeObj.hours) || !this.validateMinutes(timeObj.minutes) || props.showSeconds && !this.validateSeconds(timeObj.seconds)) {
      timeObj.error = true;
      timeObj.errorMessage = _I18nManager.default.t('TimePicker', 'invalid_time');
    } else {
      // Check if time options are disabled
      if (props.disabledHours) {
        var disabledHours = props.disabledHours(timeObj) || [];

        if (disabledHours.indexOf(timeObj.hours) > -1) {
          timeObj.hours = undefined;
          timeObj.error = true;
          timeObj.errorMessage = _I18nManager.default.t('TimePicker', 'disabled_error').replace('%s', _I18nManager.default.t('TimePicker', 'hours'));
        }
      }

      if (props.disabledMinutes) {
        var disabledMinutes = props.disabledMinutes(timeObj) || [];

        if (disabledMinutes.indexOf(timeObj.minutes) > -1) {
          timeObj.minutes = undefined;
          timeObj.error = true;
          timeObj.errorMessage = _I18nManager.default.t('TimePicker', 'disabled_error').replace('%s', _I18nManager.default.t('TimePicker', 'minutes'));
        }
      }

      if (props.disabledSeconds) {
        var disabledSeconds = props.disabledSeconds(timeObj) || [];

        if (disabledSeconds.indexOf(timeObj.seconds) > -1) {
          timeObj.seconds = undefined;
          timeObj.error = true;
          timeObj.errorMessage = _I18nManager.default.t('TimePicker', 'disabled_error').replace('%s', _I18nManager.default.t('TimePicker', 'seconds'));
        }
      } // Check if time options are filtered


      if (props.filteredHours) {
        var filteredHours = props.filteredHours(timeObj) || [];

        if (filteredHours.indexOf(timeObj.hours) === -1) {
          timeObj.hours = undefined;
          timeObj.error = true;
          timeObj.errorMessage = _I18nManager.default.t('TimePicker', 'filtered_error').replace('%s', _I18nManager.default.t('TimePicker', 'hours'));
        }
      }

      if (props.filteredMinutes) {
        var filteredMinutes = props.filteredMinutes(timeObj) || [];

        if (filteredMinutes.indexOf(timeObj.minutes) === -1) {
          timeObj.minutes = undefined;
          timeObj.error = true;
          timeObj.errorMessage = _I18nManager.default.t('TimePicker', 'filtered_error').replace('%s', _I18nManager.default.t('TimePicker', 'minutes'));
        }
      }

      if (props.filteredSeconds) {
        var filteredSeconds = props.filteredSeconds(timeObj) || [];

        if (filteredSeconds.indexOf(timeObj.seconds) === -1) {
          timeObj.seconds = undefined;
          timeObj.error = true;
          timeObj.errorMessage = _I18nManager.default.t('TimePicker', 'filtered_error').replace('%s', _I18nManager.default.t('TimePicker', 'seconds'));
        }
      }

      if (!timeObj.error) {
        timeObj.error = false;
        timeObj.errorMessage = '';
      }
    }

    return timeObj;
  },

  /**
   * Parse time string value into object with hours, minutes and seconds in 24 hour time
   * @param {string} value - Time value
   * @param {object} props - TimePicker props
   * @returns {{hours: *, minutes: *, seconds: *}} - Time object
   */
  parseInputValue: function parseInputValue(value, props) {
    var regExpStr;

    if (props.format12Hours) {
      // If application has different translation for am or pm, we should parse based on that
      var am = _I18nManager.default.t('TimePicker', 'AM');

      var pm = _I18nManager.default.t('TimePicker', 'PM');

      var ampmRegExpStr = "".concat(am.toUpperCase(), "|").concat(am.toLowerCase(), "|").concat(pm.toUpperCase(), "|").concat(pm.toLowerCase());

      if (props.showSeconds) {
        regExpStr = "^(\\d?\\d):(\\d\\d):(\\d\\d) (".concat(ampmRegExpStr, ")$");
      } else {
        regExpStr = "^(\\d?\\d):(\\d\\d) (".concat(ampmRegExpStr, ")$");
      }
    } else if (props.showSeconds) {
      regExpStr = '^(\\d\\d):(\\d\\d):(\\d\\d)$';
    } else {
      regExpStr = '^(\\d\\d):(\\d\\d)$';
    }

    var timeRegex = new RegExp(regExpStr); // First match group is hours, then minutes, then seconds and then AM/PM

    var matches = value.match(timeRegex);
    var hours, minutes, seconds;

    if (matches) {
      hours = parseInt(matches[1], 10);
      minutes = parseInt(matches[2], 10);
      seconds = parseInt(matches[3], 10);

      if (props.format12Hours) {
        var ampm;

        if (props.showSeconds) {
          ampm = matches[4];
        } else {
          ampm = matches[3];
        }

        if (ampm) {
          if (hours < 1 || hours > 12) {
            hours = undefined;
          } else if (ampm.toLowerCase() === 'pm') {
            if (hours !== 12) {
              hours += 12;
            }
          } else if (ampm.toLowerCase() === 'am') {
            if (hours === 12) {
              hours = 0;
            }
          }
        }
      }
    }

    hours = this.validateHours(hours) ? hours : undefined;
    minutes = this.validateMinutes(minutes) ? minutes : undefined;
    var timeObj = {
      hours: hours,
      minutes: minutes
    };

    if (props.showSeconds) {
      timeObj.seconds = this.validateSeconds(seconds) ? seconds : undefined;
    }

    return timeObj;
  },

  /**
   * Validate hours
   * @param {number} hours - Hours (0-23)
   * @returns {boolean} - Whether or not hours is valid
   */
  validateHours: function validateHours(hours) {
    return hours >= 0 && hours <= 23;
  },

  /**
   * Validate minutes
   * @param {number} minutes - Minutes (0-59)
   * @returns {boolean} - Whether or not minutes is valid
   */
  validateMinutes: function validateMinutes(minutes) {
    return minutes >= 0 && minutes <= 59;
  },

  /**
   * Validate seconds
   * @param {number} seconds - Seconds (0-59)
   * @returns {boolean} - Whether or not seconds is valid
   */
  validateSeconds: function validateSeconds(seconds) {
    return seconds >= 0 && seconds <= 59;
  }
};
exports.default = _default;